<template>
  <div
    v-bind="$attrs"
    v-on="$listeners"
    :style="style.root"
  >
    <div :style="style.container">
      <TextButtonMuted
        :style="style.buttonBack"
        @click="goToAdminSurveysPage"
      >
        <GoogleMaterialIconPrimary
          :content="'arrow_back'"
          :iconColor="iconBackColor"
          :style="style.iconBack"
        />
        Back to survey selection
      </TextButtonMuted>
      <div :style="style.headerContainer">
        <HeaderLargePrimary>
          {{ survey.name }}
        </HeaderLargePrimary>
        <div :style="style.headerRight">
          <template v-if="survey.isExpired">
            <TextWarningPrimary>
              Expired
            </TextWarningPrimary>
          </template>
          <template v-else-if="survey.isPublished">
            <TextRegular>
              Published
            </TextRegular>
            <TextMutedSecondary :style="style.publishDate">
              {{ publishDate }}
            </TextMutedSecondary>
          </template>
          <template v-else-if="!survey.isPublished">
            <TextButtonPrimary
              v-if="!survey.isPublished"
              @click="openModalToPublishSurvey"
            >
              Publish
            </TextButtonPrimary>
          </template>
          <DropdownMorePrimary
            :dropdownLeft="attrs.surveyDropdown.dropdownLeft"
            :dropdownMinWidth="attrs.surveyDropdown.dropdownMinWidth"
            :options="getSurveyDropdownOptions"
            :style="style.surveyDropdown"
          />
        </div>
      </div>
      <TabPrimary
        v-model="tabIndex"
        :tabs="getTabs"
        :style="style.tab"
      />
      <template v-if="tabIndex === 0">
        <TextRegular :style="style.headline">
          Select and add all the skills you have.
        </TextRegular>
        <FlexLayoutPrimary>
          <template v-slot:left>
            <TextCardLabelPrimary :style="style.cardLabel">
              CATEGORIES
            </TextCardLabelPrimary>
            <CardPrimary :style="style.cardSurveyCategories">
              <RadioListPrimary
                v-model="selectedSurveyCategoryId"
                :disableDeselect="true"
                :items="surveyCategories"
                :labelKey="'name'"
                :truncate="true"
              />
            </CardPrimary>
          </template>
          <template v-slot:center>
            <TextCardLabelPrimary :style="style.cardLabel">
              SKILLS
            </TextCardLabelPrimary>
            <CardPrimary :style="style.cardSurveyCategorySkills">
              <RadioListPrimary
                v-model="selectedSurveyCategorySkillId"
                :disableDeselect="true"
                :items="selectedSurveyCategorySkills"
                :labelKey="'name'"
                :truncate="true"
              />
            </CardPrimary>
          </template>
          <template v-slot:right>
            <SkillDetail
              :isPreview="true"
              :ownedSkillIds="[]"
              :survey="survey"
              :surveyCategorySkill="selectedSurveyCategorySkill"
            />
          </template>
        </FlexLayoutPrimary>
      </template>
      <template v-else-if="tabIndex === 1">
        <AdminSurveyResponses
          :survey="survey"
          :surveyCategorySkills="surveyCategorySkills"
          :style="style.responses"
        />
      </template>
      <template v-else-if="tabIndex === 2">
        <div :style="style.downloadContainer">
          <AdminSurveyDownload
            :survey="survey"
            @download-file="downloadSurveyFile"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
  import AdminSurveyDownload from '@survey/modules/admin-survey-download'
  import AdminSurveyResponses from '@survey/modules/admin-survey-responses'
  import ApiSurveyDownloadFile from '@js/api/survey-download-file'
  import CardPrimary from '@modules/v2/card/card-primary'
  import DropdownMorePrimary from '@modules/v2/dropdown/dropdown-more-primary'
  import FlexLayoutPrimary from '@survey/modules/flex-layout-primary'
  import GoogleMaterialIconPrimary
    from '@modules/v2/icon/google-material-icon-primary'
  import HeaderLargePrimary from '@modules/v2/header/header-large-primary'
  import moment from 'moment'
  import RadioListPrimary from '@modules/v2/radio-list/radio-list-primary'
  import SkillDetail from '@survey/modules/skill-detail'
  import TabPrimary from '@modules/v2/tab/tab-primary'
  import TextButtonMuted from '@modules/v2/button/text-button-muted'
  import TextButtonPrimary from '@modules/v2/button/text-button-primary'
  import TextCardLabelPrimary from '@modules/v2/text/text-card-label-primary'
  import TextMutedSecondary from '@modules/v2/text/text-muted-secondary'
  import TextRegular from '@modules/v2/text/text-regular'
  import TextWarningPrimary from '@modules/v2/text/text-warning-primary'
  import { DIALOG_NAMES, MODAL_WINDOW_NAMES } from '@js/constants'
  import { downloadCsvFile } from '@js/file-utils'
  import { Layout } from '@js/styles'

  export default {
    name: 'AdminSurveyView',
    inheritAttrs: false,
    components: {
      AdminSurveyDownload,
      AdminSurveyResponses,
      CardPrimary,
      DropdownMorePrimary,
      FlexLayoutPrimary,
      GoogleMaterialIconPrimary,
      HeaderLargePrimary,
      RadioListPrimary,
      SkillDetail,
      TabPrimary,
      TextButtonMuted,
      TextButtonPrimary,
      TextCardLabelPrimary,
      TextMutedSecondary,
      TextRegular,
      TextWarningPrimary,
    },
    props: {
      survey: {
        type: Object,
        required: false,
        defualt: () => {},
      },
      surveyCategorySkills: {
        type: Array,
        required: false,
        default: () => [],
      },
      tenantUser: {
        type: Object,
        required: false,
        default: () => {},
      },
    },
    data: function () {
      return {
        iconBackColor: 'none',
        isDownloading: false,
        selectedSurveyCategoryId: null,
        selectedSurveyCategorySkillId: null,
        tabIndex: 0,
        attrs: {
          surveyDropdown: {
            dropdownLeft: '-118px',
            dropdownMinWidth: '160px',
          },
        },
        style: {
          buttonBack: {
            padding: Layout.container.md.padding,
          },
          cardLabel: {
            height: Layout.space.md,
            margin: `0 0 ${Layout.space.md} 0`,
          },
          cardSurveyCategories: {
            height: 'calc(100% - 32px)',
            overflow: 'scroll',
            padding: Layout.space.sm,
            width: '100%',
          },
          cardSurveyCategorySkills: {
            height: 'calc(100% - 32px)',
            overflow: 'scroll',
            padding: Layout.space.sm,
            width: '100%',
          },
          container: {
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            margin: `${Layout.space.lg} auto`,
            maxWidth: Layout.container.primary.maxWidth,
            width: '100%',
          },
          downloadContainer: {
            padding: `${Layout.space.md} ${Layout.space.lg}`,
          },
          headerContainer: {
            alignItems: 'center',
            display: 'flex',
            height: '30px',
            justifyContent: 'space-between',
            margin: `${Layout.space.lg} 0`,
            padding: Layout.container.md.padding,
            width: '100%',
          },
          headerRight: {
            alignItems: 'center',
            display: 'flex',
            height: '100%',
            justifyContent: 'flex-end',
          },
          headline: {
            padding: `${Layout.space.lg} ${Layout.space.lg}`,
          },
          iconBack: {
            display: 'inline-block',
            margin: `0 ${Layout.space.xs} 0 0`,
          },
          publishDate: {
            margin: `0 0 0 ${Layout.space.sm}`,
          },
          responses: {
            margin: `${Layout.space.md} 0 0 0`,
            padding: `0 ${Layout.space.lg}`,
          },
          root: {
            alignItems: 'center',
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            justifyContent: 'flex-start',
            width: '100%',
          },
          surveyDropdown: {
            margin: `0 0 0 ${Layout.space.md}`,
          },
          tab: {
            margin: `${Layout.space.lg} 0 ${Layout.space.md}`,
            padding: Layout.container.md.padding,
            width: '100%',
          }
        },
      };
    },
    mounted: function () {
      if (this.surveyCategories.length > 0) {
        this.selectedSurveyCategoryId = this.surveyCategories[0].id;
      }

      if (this.selectedSurveyCategorySkills.length > 0) {
        this.selectedSurveyCategorySkillId = this.selectedSurveyCategorySkills[0].id;
      }
    },
    computed: {
      expirationDate: function () {
        return (
          this.survey.expiresAt === undefined ||
          this.survey.expiresAt === null
        ) ? 'Never expire'
          : moment(this.survey.expiresAt).format('MMM D YYYY');
      },
      getSurveyDropdownOptions: function () {
        return [
          {
            id: 1,
            label: 'Distribute',
            adminRequired: true,
            disabled: !this.survey.isPublished,
            hookFunction: () => {
              if (!this.survey.isPublished) {
                return;
              }
              const params = {
                survey: this.survey,
              };
              this.$modal.show(MODAL_WINDOW_NAMES.SHARE_SURVEY_PRIMARY, params);
            },
          },
          {
            id: 2,
            label: 'Rename survey',
            adminRequired: true,
            hookFunction: () => {
              const params = {
                survey: this.survey,
              };
              this.$modal.show(MODAL_WINDOW_NAMES.RENAME_SURVEY_PRIMARY, params);
            },
          },
          {
            id: 3,
            label: 'Set expiration',
            adminRequired: true,
            disabled: !this.survey.isPublished,
            hookFunction: () => {
              if (!this.survey.isPublished) {
                return;
              }
              const params = {
                survey: this.survey,
              };
              this.$modal.show(MODAL_WINDOW_NAMES.CONFIGURE_SURVEY_PRIMARY, params);
            },
          },
          {
            id: 4,
            label: 'Delete survey',
            adminRequired: true,
            hookFunction: () => {
              const params = {
                survey: this.survey,
              };
              this.$modal.show(DIALOG_NAMES.DELETE_SURVEY_PRIMARY, params);
            },
          },
        ];
      },
      getTabs: function () {
        return [
          {
            label: 'Preview',
            disabled: this.isDownloading,
          },
          {
            label: 'Responses',
            disabled: !this.survey.isPublished || this.isDownloading,
          },
          {
            label: 'Download',
            disabled: this.isDownloading,
          },
        ];
      },
      publishDate: function () {
        return (
          this.survey.publishedAt === undefined ||
          this.survey.publishedAt === null
        ) ? ''
          : moment(this.survey.publishedAt).format('MMM D YYYY');
      },
      selectedSurveyCategorySkill: function () {
        if (this.selectedSurveyCategorySkillId === null) {
          return {};
        }
        return this.surveyCategorySkills
          .filter(elem => elem?.id === this.selectedSurveyCategorySkillId)[0];
      },
      selectedSurveyCategorySkills: function () {
        if (this.selectedSurveyCategoryId === null) {
          return [];
        }

        return this.surveyCategorySkills
          .filter(
            elem => elem?.surveyCategory?.id === this.selectedSurveyCategoryId
          )
          .map(elem => {
            return {
              ...elem,
              name: elem?.skill?.name,
             };
          })
          .sort((a, b) => a.displayOrder - b.displayOrder);
      },
      surveyCategories: function () {
        return this.surveyCategorySkills
          .map(elem => elem.surveyCategory)
          .filter(
            (elem, index, array) => {
              return array.findIndex(item => item.id === elem.id) === index
            }
          )
          .sort((a, b) => a.displayOrder - b.displayOrder);
      },
    },
    methods: {
      downloadFile: async function (res, fileNamePrefix = '') {
        try {
          const url = await window.URL.createObjectURL(new Blob([res.data]));
          const link = await document.createElement('a');
          link.href = await url;

          await link.setAttribute(
            'download',
            `${this.survey.name}_${moment().format('MMDDYYYY')}.csv`,
          );
          await document.body.appendChild(link);
          await link.click();
          await document.body.removeChild(link);
          await this.$store.commit(
            'notification/setMessage',
            'Downloaded file',
          );
        } catch (error) {
          console.error(error);
          this.$store.commit(
            'notification/setWarning',
            'Unexpected error',
          );
        }
      },
      downloadSurveyFile: async function (mode, fileName) {
        try {
          const queryParams = await {
            mode: mode,
          };
          const payload = await {};
          const api = await new ApiSurveyDownloadFile(this.survey.code);
          const res = await api.post(payload, queryParams);
          await downloadCsvFile(res.data, fileName);
        } catch (error) {
          console.error(error);
          this.$store.commit(
            'notification/setWarning',
            'Failed to download data',
          );
        }
      },
      goToAdminSurveysPage: function () {
        this.$router.push({
          name: 'admin-surveys',
        });
      },
      openModalToPublishSurvey: function () {
        const params = {
          survey: this.survey,
        };
        this.$modal.show(MODAL_WINDOW_NAMES.PUBLISH_SURVEY_PRIMARY, params);
      },
    },
    watch: {
      selectedSurveyCategoryId: function (newVal, oldVal) {
        this.selectedSurveyCategorySkillId = (
          this.selectedSurveyCategorySkills.length > 0
            ? this.selectedSurveyCategorySkills[0].id
            : null
        );
      },
    },
  };
</script>
